import { ManageUnitsUnitGroupEnumType } from '@mark43/cad-api';

/**
 * The shape the staff count rules need. Any `ManageUnitsUnitView` satisfies it.
 */
export type StaffCountUnit = {
    id: number;
    manageUnitGroup: ManageUnitsUnitGroupEnumType;
    staffCount?: number;
    suggestedStaffCount?: number;
};

/** The largest count the server saves at dispatch (`DispatchStaffCountService.MAX_STAFF_COUNT_AT_DISPATCH`). */
export const MAX_STAFF_COUNT_AT_DISPATCH = 10;

/**
 * The recommendation's crew applies only to a unit this dispatch newly assigns that the current plan still
 * recommends: its row is in the RECOMMENDED group and it is not already on the event. The group check
 * matters because a row can carry a `suggestedStaffCount` it should not use: the server sets it before
 * grouping, so a recommended unit busy elsewhere lands in UNAVAILABLE with it, and the async store demotes
 * a superseded recommendation to AVAILABLE without clearing it. Counts the server would reject (outside
 * 1..MAX_STAFF_COUNT_AT_DISPATCH) are ignored. Nothing applies when `writeEnabled` is false (see
 * `isDispatchStaffCountWriteEnabled`): the drawer must not show or count a suggestion the server will not save.
 */
export function getSuggestedStaffCountForDispatch(
    unit: StaffCountUnit,
    initiallyDispatchedUnitIds: ReadonlySet<number>,
    writeEnabled: boolean
): number | undefined {
    if (
        !writeEnabled ||
        unit.manageUnitGroup !== 'RECOMMENDED' ||
        initiallyDispatchedUnitIds.has(unit.id)
    ) {
        return undefined;
    }
    const suggested = unit.suggestedStaffCount;
    return suggested !== undefined &&
        suggested !== null &&
        suggested > 0 &&
        suggested <= MAX_STAFF_COUNT_AT_DISPATCH
        ? suggested
        : undefined;
}

/** The count the drawer shows for a unit: the suggestion when dispatch will write it, else the stored count. */
export function getShownStaffCount(
    unit: StaffCountUnit,
    initiallyDispatchedUnitIds: ReadonlySet<number>,
    writeEnabled: boolean
): number | undefined {
    return (
        getSuggestedStaffCountForDispatch(unit, initiallyDispatchedUnitIds, writeEnabled) ??
        unit.staffCount
    );
}

/**
 * The event's staff count.
 *
 * When the dispatch write is on, it is the count the event will have after Save: shown counts summed
 * over every selected unit (dispatched units still selected, plus newly selected ones, using the
 * suggested crew where dispatch will write it). Released units drop out. It always means "after Save",
 * whether or not a suggestion is in play, because departments that use recommendations build them
 * around the intended number of people on the call, so the dispatcher needs that number to stay
 * consistent as they select and release units.
 *
 * When the write is off, it is the pre-recommendation total: the sum of the already-dispatched units'
 * stored staff counts, unaffected by the current selection. That keeps the total unchanged for every
 * department that has not turned the write on.
 */
export function computeEventStaffCount(
    selectedUnits: StaffCountUnit[],
    alreadyDispatchedUnits: ReadonlyArray<{ staffCount?: number }>,
    initiallyDispatchedUnitIds: ReadonlySet<number>,
    writeEnabled: boolean
): number {
    if (!writeEnabled) {
        return alreadyDispatchedUnits.reduce((total, unit) => total + (unit.staffCount ?? 0), 0);
    }
    return selectedUnits.reduce(
        (total, unit) =>
            total + (getShownStaffCount(unit, initiallyDispatchedUnitIds, writeEnabled) ?? 0),
        0
    );
}

export type StaffCountSubmission = {
    unitIdToStaffCount?: Record<string, number>;
    unitIdToStaffCountBaseline?: Record<string, number>;
};

/**
 * The staff count part of the dispatch form: the suggested crew for each selected unit it applies to,
 * sent only where it differs from the stored count. The baseline is the stored count the drawer showed,
 * so the server can skip a unit whose count changed since; a unit with no stored count has no baseline
 * entry. Empty when the write is off.
 */
export function buildStaffCountSubmission(
    selectedUnits: StaffCountUnit[],
    initiallyDispatchedUnitIds: ReadonlySet<number>,
    writeEnabled: boolean
): StaffCountSubmission {
    if (!writeEnabled) {
        return {};
    }
    const unitIdToStaffCount: Record<string, number> = {};
    const unitIdToStaffCountBaseline: Record<string, number> = {};
    for (const unit of selectedUnits) {
        const suggested = getSuggestedStaffCountForDispatch(
            unit,
            initiallyDispatchedUnitIds,
            writeEnabled
        );
        if (suggested === undefined || suggested === unit.staffCount) {
            continue;
        }
        unitIdToStaffCount[unit.id] = suggested;
        if (unit.staffCount !== undefined && unit.staffCount !== null) {
            unitIdToStaffCountBaseline[unit.id] = unit.staffCount;
        }
    }
    if (Object.keys(unitIdToStaffCount).length === 0) {
        return {};
    }
    return { unitIdToStaffCount, unitIdToStaffCountBaseline };
}

/**
 * Whether dispatch saves the recommendation's suggested crew. Both the unit staff count feature and the
 * department's dispatch staff count write switch must be on; the server checks the same two.
 */
export function isDispatchStaffCountWriteEnabled(features: {
    staffCountEnabled: boolean;
    prologResponsePlansDispatchStaffCountWriteEnabled: boolean;
}): boolean {
    return features.staffCountEnabled && features.prologResponsePlansDispatchStaffCountWriteEnabled;
}
